/**
 * GenericEquipment - timing logic for fire actions used by weapon-like components.
 * Tracks when firing starts and ensures repeated shots respect a configurable interval.
 * Derived classes implement `doFire` for custom behavior. Handles single-fire modes
 * where only one shot occurs per button press.
 */
import { property } from '../../utils/serialization/decorator.js';
import { SceneComponent } from '../SceneComponent.js';

import type { SceneComponentOptions } from '../SceneComponent.js';
import type { IEquipment } from './Equipment.js';


export interface GenericEquipmentOptions extends SceneComponentOptions {
  fireInterval?: number;
  isSingleFire?: boolean;
}


export abstract class GenericEquipment extends SceneComponent implements IEquipment {



  private fireFlag: boolean = false;
  private fireCount: number = 0;
  private lastFireTime: number = 0;

  @property({ type: 'number', min: 0.01, step: 0.1, decimals: 2 })
  protected fireInterval: number = 0.5;

  @property()
  protected isSingleFire: boolean = false;

  public override initialize(options?: GenericEquipmentOptions): void {
    super.initialize(options);
    this.fireInterval = options?.fireInterval ?? this.fireInterval;
    this.isSingleFire = options?.isSingleFire ?? this.isSingleFire;
  }

  public beginFire(): boolean {
    this.fireFlag = true;
    this.checkFire();
    return true;
  }

  public endFire(): boolean {
    this.fireFlag = false;
    return true;
  }

  public beginAltFire(): boolean {
    return false;
  }

  public endAltFire(): boolean {
    return false;
  }

  public reload(): boolean {
    return false;
  }

  public override tickPrePhysics(deltaTime: number): void {
    this.checkFire();
  }

  //====================================================================================================

  protected checkFire(): void {
    if (this.fireFlag) {
      if (this.isSingleFire && this.fireCount > 0) {
        return;
      }
      const currentTime = this.getWorld()?.getGameTime() ?? 0;
      let fireTime = this.lastFireTime + this.fireInterval;
      while (fireTime <= currentTime) {
        if (!this.fireCount) {
          fireTime = currentTime;
        }
        this.fireCount++;
        this.doFire();
        this.lastFireTime = fireTime;
        fireTime += this.fireInterval;
      }
    } else {
      this.fireCount = 0;
    }
  }

  protected abstract doFire(): void;
}
